import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import { Badge } from '@/components/shadcn-ui/badge';
import { Button } from '@/components/shadcn-ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn-ui/dropdown-menu';
import { Filter, Download, MoreHorizontal, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { appAxios } from '@/network/base/appAxios';
import PermitStatusBadge from '@/components/PermitStatusBadge';
import { toast } from 'sonner';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/shadcn-ui/input';
import PermitFilters from '@/components/PermitFilters';
import { useUserStore } from '@/stores/useUser.store';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn-ui/dialog';

const Requests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [admins, setAdmins] = useState([]);
  const adminDetails = useUserStore(state => state.adminDetails);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);

  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const response = await appAxios.get('/Admin/v1.0/admin-list', {
        params: { page: 1, limit: 100 },
      });
      if (response.data?.data?.data) {
        const filteredAdmins = response.data.data.data.filter(admin => admin.user_email !== adminDetails.user_email);
        setAdmins(filteredAdmins);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Failed to fetch admin list');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const getRequests = async (page = 1, limit = 10, search = '', filterParams = {}) => {
    setLoading(true);
    try {
      const params = { page, limit };

      // Add search parameter
      if (search && search.trim()) {
        params.search = search.trim();
      }

      // Add filter parameters
      if (filterParams.jurisdiction) params.portal_name = filterParams.jurisdiction;
      if (filterParams.permitType) params.permit_type = filterParams.permitType;
      if (filterParams.status) params.permit_status = filterParams.status;
      if (filterParams.automation) params.queue_status = filterParams.automation;
      if (filterParams.assignedTo) params.assigned_to = filterParams.assignedTo;

      const response = await appAxios.get('/Admin/v1.0/permits', { params });

      if (response.data?.data?.data) {
        // Transform API data to match table structure
        const transformedData = response.data.data.data.map(permit => ({
          id: permit._id,
          customerName: permit?.user_id?.fullname || 'N/A',
          customerEmail: permit?.user_id?.user_email || 'N/A',
          permitId: permit._id.slice(-8) || permit.application_id,
          type: getPermitType(permit.permit_type),
          project: permit.project_name || 'N/A',
          address: permit.portal_address || 'N/A',
          location: permit.portal_name || 'N/A',
          workDescription: permit.work_description || 'N/A',
          submitted: formatDate(permit.createdAt),
          unit: permit.unit || 'N/A',
          readyToFill: permit.ready_to_fill,
          submitToCity: permit.submit_to_city,
          automation: permit?.queue_id?.status || 'Pending',
          permit_status: permit?.permit_status || "new",
          permit_manual_status: permit?.permit_manual_status || "pending_zermit_fees",
          assignedTo: permit?.assigned_to?.fullname || "N/A",

          // static fields 
          invoice: "Pending"
        }));

        setRequests(transformedData);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching permits:', error);
      toast.error('Failed to fetch permits', {
        description: error.response?.data?.message || error.message || 'An error occurred while fetching permits'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRequests(1, pagination.limit, '');
  }, []);
  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);


  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    getRequests(1, pagination.limit, debouncedSearch, filters);
  }, [debouncedSearch, filters]);

  useEffect(() => {
    fetchAdmins();
  }, []);


  const handleAssignAdmin = (permitId, adminId, adminName) => {
    setPendingAssignment({ permitId, adminId, adminName });
    setConfirmDialogOpen(true);
  };

  const confirmAssignment = async () => {
    try {
      const { permitId, adminId, adminName } = pendingAssignment;
      const response = await appAxios.post(`/Admin/v1.0/assign-permit/${permitId}`, { adminId: adminId });
      if (response.status == 200) {
        toast.success(`Assigned to ${adminName}`);
      } else {
        toast.error('Failed to assign admin');
      }
      getRequests(pagination.page, pagination.limit);
    } catch (error) {
      console.error('Error assigning admin:', error);
      toast.error('Failed to assign admin');
    } finally {
      setConfirmDialogOpen(false);
      setPendingAssignment(null);
    }
  };

  const getPermitType = type => {
    const types = {
      '0': 'Bldg Alter Repair',
      '1': 'Electrical Vehicle Charger',
      '2': 'Bldg Alter Repair',
      '3': 'Fire Sprinkler',
      '4': 'HVAC',
      '5': 'Plumbing',
      '6': 'Pressure Vessel',
      '7': 'Temporary Special Event',
    };
    return types[type] || `${type}`;
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const highlightText = (text, query) => {
    if (!query.trim() || !text) return text;

    const parts = text.toString().split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={index} className="bg-yellow-200 dark:bg-yellow-900/50 text-inherit font-medium rounded px-0.5">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  const handlePageChange = page => {
    getRequests(page, pagination.limit, debouncedSearch, filters);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const getStatusBadge = status => {
    const variants = {
      Submitted: 'default',
      Ready: 'success',
      'In Progress': 'warning',
      New: 'info',
      pending: 'pending',
      running: "running",
      completed: "success",
      success: "success",
      failed: "destructive",
      creds_not_found: "warning",

    };
    const displayText = status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    return <Badge variant={variants[status] || 'default'}>{displayText}</Badge>;
  };

  const columns = [
    {
      key: 'permitId',
      label: 'Permit ID',
      cellClassName: 'font-medium',
      render: (value, row) => (
        <button
          onClick={() => navigate(`/admin/dashboard/permits/${row.id}`)}
          className="text-primary hover:underline font-medium cursor-pointer"
        >
          {highlightText(value, debouncedSearch)}
        </button>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (value) => highlightText(value, debouncedSearch),
    },
    {
      key: 'project',
      label: 'Project',
      render: (value, row) => (
        <div className="max-w-[200px]">
          <div className="truncate font-medium" title={value}>
            {highlightText(value, debouncedSearch)}
          </div>
        </div>
      ),
    },
    {
      key: 'customerEmail',
      label: 'Customer',
      render: (value, row) => (
        <div className="max-w-[200px]">
          <div className="truncate font-medium" title={value}>
            {highlightText(row.customerName, debouncedSearch)}
          </div>
          <div className="text-xs text-muted-foreground truncate" title={row.customerEmail}>
            {highlightText(row.customerEmail, debouncedSearch)}
          </div>
        </div>
      ),
    },
    {
      key: 'address',
      label: 'Address',
      render: (value, row) => (
        <div className="max-w-[250px]">
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="truncate" title={value}>
              {highlightText(value, debouncedSearch)}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {highlightText(row.location, debouncedSearch)}
          </div>
        </div>
      ),
    },
    // {
    //   key: 'unit',
    //   label: 'Unit',
    //   cellClassName: 'text-muted-foreground',
    // }, 
    {
      key: 'manual_status',
      label: 'Manual Status',
      render: (value, row) => <PermitStatusBadge status={row?.permit_manual_status || "pending_zermit_fees"} />,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => <PermitStatusBadge status={row?.permit_status || "new"} />,
      // render: (value, row) => <PermitStatusBadge status={row?.permit_status == "automation_failed" ? "new" : row?.permit_status || "new"} />,
    },
    {
      key: 'automation',
      label: 'Automation',
      render: value => getStatusBadge(value),
    },
    {
      key: 'invoice',
      label: 'Invoice',
      render: value => <Badge variant="invoicePending">{value}</Badge>,
    },
    // {
    //   key: 'assignedTo',
    //   label: 'Assigned To',
    // },
    {
      key: 'submitted',
      label: 'Submitted',
      cellClassName: 'text-muted-foreground',
    },
    {
      key: 'action',
      label: 'Assign To',
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-2" disabled={row.assignedTo !== "N/A"}>
              <span className="truncate w-[120px]">
                {row.assignedTo == "N/A" ? 'Select...' : row.assignedTo}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px]">
            {loadingAdmins ? (
              <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
            ) : admins.length === 0 ? (
              <DropdownMenuItem disabled>No admins available</DropdownMenuItem>
            ) : (
              admins.map(admin => (
                <DropdownMenuItem
                  key={admin._id}
                  onClick={() => handleAssignAdmin(row.id, admin._id, admin.fullname)}
                >
                  {admin.fullname}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
    // {
    //   key: 'actions',
    //   label: '',
    //   render: (_, row) => (
    //     <DropdownMenu>
    //       <DropdownMenuTrigger asChild>
    //         <Button variant="ghost" size="icon" className="h-8 w-8">
    //           <MoreHorizontal className="h-4 w-4" />
    //         </Button>
    //       </DropdownMenuTrigger>
    //       <DropdownMenuContent align="end">
    //         <DropdownMenuItem>View Details</DropdownMenuItem>
    //         <DropdownMenuItem>Edit</DropdownMenuItem>
    //         <DropdownMenuItem>Download</DropdownMenuItem>
    //         <DropdownMenuItem className="text-destructive">Delete</DropdownMenuItem>
    //       </DropdownMenuContent>
    //     </DropdownMenu>
    //   ),
    // },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Requests</h1>
          <div>
            <p className="text-muted-foreground">
              {debouncedSearch ? (
                <>
                  Found {pagination.total} result{pagination.total !== 1 ? 's' : ''} for "{debouncedSearch}"
                </>
              ) : (
                'Manage all permit requests'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search permits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
            />
            {loading && searchQuery && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              </div>
            )}
            {searchQuery && !loading && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            className="gap-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4" />
            Filters
            {Object.values(filters).filter(v => v && v !== '').length > 0 && (
              <span className="ml-1 rounded-full bg-primary-foreground text-primary px-2 py-0.5 text-xs font-medium">
                {Object.values(filters).filter(v => v && v !== '').length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <PermitFilters
          onFilterChange={handleFilterChange}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Data Table */}
      <DataTable
        data={requests}
        columns={columns}
        searchable={false}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No permit requests found"
        className="whitespace-nowrap"
      />

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Assignment</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to assign this permit to <strong>{pendingAssignment?.adminName}</strong>?
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmAssignment}>
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Requests;
